import { HoneypotField } from '@components/HoneypotField'
import { useLocalization } from '@components/Localization'
import { useNotifications } from '@components/NotificationsProvider'
import { Box, Button, Container, OldHeading, OldText } from '@gassan-ui'
import {
  useAdminEventFormQuery,
  useCreateFormSubmissionMutation,
  useFormSubmissionQuery,
} from '@generated'
import { capitalizeWords } from '@lib/capitalize-words'
import { checkHoneypot } from '@lib/check-honeypot'
import { cn } from '@lib/cn'
import { genericPushToTrackingLayer } from '@lib/data-layer-social-ads/generic-push-to-tracking-layer'
import { useMe } from '@lib/hooks/use-me'
import { CbAdminEventForm } from '@lib/kontent/models/CbAdminEventForm'
import { Form, Formik } from 'formik'
import { useTranslation } from 'next-i18next'
import { rem } from 'polished'
import { FC, useState } from 'react'
import { Fields } from './Fields'
import { Values } from './types'
import { getSchema } from './utils'

type Props = CbAdminEventForm

/**
 * Note: we have a `DebugAdminEventForm` component that can be used to test the admin event form component.
 */
const AdminEventForm: FC<Props> = ({ title, description, form_id }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation(['notifications', 'forms', 'other', 'navigation', 'user-errors'])
  const [formQuery] = useAdminEventFormQuery({ variables: { id: form_id.value } })
  const [successfullySubmitted, setSuccessfullySubmitted] = useState(false)
  const { parseGraphQlError } = useLocalization()
  const { me } = useMe()

  const [submissionQuery] = useFormSubmissionQuery({
    variables: { id: form_id.value },
    pause: !!me,
    requestPolicy: 'network-only',
  })
  const previousSubmission = submissionQuery.data?.userFormSubmission

  const notifications = useNotifications()
  const [, submitForm] = useCreateFormSubmissionMutation()

  const showSuccessNotification = () => {
    notifications.addNotification({
      variant: 'confirmation',
      description: t('requestSent', { ns: 'notifications' }),
      label: t('weWillContactYou', { ns: 'notifications' }),
    })
  }

  const initialValues: Values = {
    attendance: previousSubmission?.values.attendance ?? true,
    firstName: previousSubmission?.firstName ?? '',
    lastName: previousSubmission?.lastName ?? '',
    email: previousSubmission?.email ?? me?.email ?? '',
    postalCode: previousSubmission?.values.postalCode ?? '',
    city: previousSubmission?.values.city ?? '',
    street: previousSubmission?.values.street ?? '',
    country: previousSubmission?.values.country ?? '',
    houseNumber: previousSubmission?.values.houseNumber ?? '',
    houseNumberSuffix: previousSubmission?.values.houseNumberSuffix ?? '',
    phoneNumber: previousSubmission?.values.phoneNumber ?? '',
    message: previousSubmission?.values.message ?? '',
    dietaryRequirements: previousSubmission?.values.dietaryRequirements ?? '',
    numberOfPersons: previousSubmission?.values.numberOfPersons ?? '',
    heardOfGassanVia: previousSubmission?.values.heardOfGassanVia ?? '',
    date: previousSubmission?.values.preferredDate ?? '',
    time: previousSubmission?.values.preferredTime ?? '',
    location: previousSubmission?.values.preferredLocation ?? '',
    newsletterSignup: previousSubmission?.values.newsletterSignup ?? false,
    website: '', // Honeypot field
  }

  if (!formQuery.data?.form) return null

  const { redirectAfterSubmission } = formQuery.data.form
  const redirects = {
    en: redirectAfterSubmission?.en ?? undefined,
    nl: redirectAfterSubmission?.nl ?? undefined,
    de: redirectAfterSubmission?.de ?? undefined,
  }

  return (
    <Container mb={{ _: '2rem', small: '4rem', large: '5rem' }} maxWidth={rem(854)}>
      <Box mb={['1.5rem', '2rem']}>
        <OldHeading variant="h3" as="h3" textAlign={['center', 'left']} mb="0">
          {title.value}
        </OldHeading>
        {description.value && (
          <OldText variant="regular" textAlign={['center', 'left']} mt={['1rem', '1rem']}>
            {description.value}
          </OldText>
        )}
      </Box>

      <Formik
        initialValues={initialValues}
        validateOnBlur
        validateOnChange
        enableReinitialize
        validate={(values) => {
          const schema = getSchema(formQuery.data.form.fields, values?.attendance ?? true)

          try {
            schema.validateSync(values, { abortEarly: false })
            return {}
          } catch (err: any) {
            const errors: any = {}
            if (err.inner) {
              err.inner.forEach((error: any) => {
                errors[error.path] = error.message
              })
            }
            return errors
          }
        }}
        onSubmit={async (values: typeof initialValues) => {
          if (previousSubmission) return

          if (!checkHoneypot()) {
            showSuccessNotification()
            setSuccessfullySubmitted(true)
            return
          }

          const { data, error } = await submitForm({
            id: form_id.value,
            input: {
              firstName: values.firstName ? capitalizeWords(values.firstName) : '',
              lastName: values.lastName ? capitalizeWords(values.lastName) : '',
              email: values.email ? values.email : '',
              values: {
                attendance: values.attendance,
                city: values.city ? values.city : '',
                street: values.street ? values.street : '',
                postalCode: values.postalCode ? values.postalCode : '',
                country: values.country ? values.country : '',
                houseNumber: values.houseNumber ? values.houseNumber : '',
                houseNumberSuffix: values.houseNumberSuffix ? values.houseNumberSuffix : '',
                phoneNumber: values.phoneNumber ? values.phoneNumber : '',
                message: values.message ? values.message : '',
                newsletter: values.newsletterSignup,
                dietaryRequirements: values.dietaryRequirements,
                numberOfPersons: values.numberOfPersons,
                heardOfGassanVia: values.heardOfGassanVia,
                preferredDate: values.date,
                preferredTime: values.time,
                preferredLocation: values.location,
                referrer: window.location.pathname,
              },
              website: values.website, // Honeypot field
            },
          })
          if (error) {
            if (process.env.NODE_ENV === 'development') {
              console.error(error)
            }
            if (error.message.includes('user-error:form-submission-limit-reached')) {
              notifications.addNotification({
                variant: 'custom',
                label: t('user-error-form-submission-limit-reached-title', { ns: 'user-errors' }),
                description: t('user-error-form-submission-limit-reached-description', {
                  ns: 'user-errors',
                }),
                icon: 'exclamation',
                color: 'error',
              })
            } else {
              notifications.addNotification({
                variant: 'error',
                label: parseGraphQlError(error.message),
              })
            }
          } else if (data && data.createFormSubmission) {
            showSuccessNotification()
            setSuccessfullySubmitted(true)

            if (['preview', 'production'].includes(process.env.NEXT_PUBLIC_STAGE)) {
              genericPushToTrackingLayer('AdminEventForm', 'Send')
            }

            if (redirects[language]) {
              window.location.href = redirects[language]
            }
          }
        }}
      >
        {({ isSubmitting }) => (
          <Form id={form_id.value}>
            <fieldset disabled={!!previousSubmission || successfullySubmitted}>
              <div
                className={cn('grid grid-cols-[repeat(12,1fr)] gap-x-4', '[&_section]:col-span-12')}
              >
                <HoneypotField />
                <Fields
                  formId={form_id.value}
                  fields={formQuery.data.form.fields}
                  showAttendance={true}
                />
                <Button variant="dark" type="submit" status={isSubmitting ? 'loading' : 'idle'}>
                  {t('submits.sendMessage', { ns: 'forms' })}
                </Button>
              </div>
            </fieldset>
          </Form>
        )}
      </Formik>
    </Container>
  )
}

export default AdminEventForm
